import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            errorCount: 0
        };
    }

    static getDerivedStateFromError(error) {
        // Mettre à jour le state pour afficher l'UI de fallback
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Log l'erreur dans la console
        console.error('🚨 ERREUR CAPTURÉE PAR ERROR BOUNDARY:', error);
        console.error('📍 Stack trace:', errorInfo);

        // Sauvegarder l'erreur dans le state
        this.setState((prev) => ({
            error,
            errorInfo,
            errorCount: prev.errorCount + 1,
        }));

        // 🔥 Envoyer l'erreur au backend
        this.logErrorToService(error, errorInfo);
    }

    logErrorToService = (error, errorInfo) => {
        // Log dans la console navigateur
        console.group('🔥 CRASH DÉTAILS');
        console.log('Message:', error.message);
        console.log('Stack:', error.stack);
        console.log('Component Stack:', errorInfo.componentStack);
        console.log('Timestamp:', new Date().toISOString());
        console.groupEnd();

        // 📤 Envoyer au backend
        fetch('https://backend.popmovie.fr/api/errors', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                message: error.message,
                stack: error.stack,
                componentStack: errorInfo.componentStack,
                timestamp: new Date().toISOString(),
                url: window.location.href
            })
        })
            .then(response => response.json())
            .then(data => {
                if (data.success) {
                    console.log('✅ Erreur loggée côté serveur:', data.logged_id);
                } else {
                    console.error('❌ Erreur lors du logging serveur:', data.error);
                }
            })
            .catch(err => {
                console.error('❌ Erreur envoi log:', err);
            });
    };

    handleReload = () => {
        // Recharger la page
        window.location.reload();
    };

    handleGoHome = () => {
        // Retourner à l'accueil
        this.setState({ hasError: false, error: null, errorInfo: null });
        window.location.href = '/';
    };

    render() {
        if (this.state.hasError) {
            return (
                <div style={{
                    minHeight: '100vh',
                    background: 'linear-gradient(135deg, #0a0a0f 0%, #1a1a2e 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px'
                }}>
                    <div style={{
                        maxWidth: '600px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '24px',
                        padding: '40px',
                        border: '2px solid rgba(239, 68, 68, 0.3)',
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
                    }}>
                        {/* Icône d'alerte */}
                        <div style={{
                            width: '80px',
                            height: '80px',
                            background: 'rgba(239, 68, 68, 0.2)',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 30px'
                        }}>
                            <AlertTriangle style={{ width: '40px', height: '40px', color: '#ef4444' }} />
                        </div>

                        {/* Titre */}
                        <h1 style={{
                            color: 'white',
                            fontSize: '2rem',
                            textAlign: 'center',
                            marginBottom: '15px',
                            fontWeight: 'bold'
                        }}>
                            Oups ! Quelque chose s'est cassé
                        </h1>

                        {/* Message */}
                        <p style={{
                            color: '#9ca3af',
                            textAlign: 'center',
                            marginBottom: '30px',
                            fontSize: '1.1rem',
                            lineHeight: '1.6'
                        }}>
                            Une erreur inattendue s'est produite. Pas de panique, tes données sont sauvegardées !
                            Recharge la page ou retourne à l'accueil.
                        </p>

                        {/* Détails de l'erreur (en mode développement) */}
                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <details style={{
                                background: 'rgba(0, 0, 0, 0.3)',
                                borderRadius: '12px',
                                padding: '15px',
                                marginBottom: '30px',
                                cursor: 'pointer'
                            }}>
                                <summary style={{
                                    color: '#ef4444',
                                    fontWeight: 'bold',
                                    marginBottom: '10px'
                                }}>
                                    📋 Détails techniques (dev only)
                                </summary>
                                <pre style={{
                                    color: '#cbd5e1',
                                    fontSize: '0.85rem',
                                    overflow: 'auto',
                                    margin: '10px 0 0 0',
                                    whiteSpace: 'pre-wrap'
                                }}>
                                    {this.state.error.toString()}
                                    {'\n\n'}
                                    {this.state.errorInfo.componentStack}
                                </pre>
                            </details>
                        )}

                        {/* Compteur d'erreurs */}
                        {this.state.errorCount > 1 && (
                            <div style={{
                                background: 'rgba(239, 68, 68, 0.1)',
                                border: '1px solid rgba(239, 68, 68, 0.3)',
                                borderRadius: '12px',
                                padding: '15px',
                                marginBottom: '30px',
                                textAlign: 'center',
                                color: '#ef4444'
                            }}>
                                ⚠️ Cette erreur s'est produite {this.state.errorCount} fois
                            </div>
                        )}

                        {/* Boutons d'action */}
                        <div style={{
                            display: 'flex',
                            gap: '15px',
                            flexWrap: 'wrap',
                            justifyContent: 'center'
                        }}>
                            <button
                                onClick={this.handleReload}
                                aria-label="Recharger la page"
                                style={{
                                    flex: '1',
                                    minWidth: '200px',
                                    background: 'linear-gradient(135deg, #E8B75A 0%, #C4462F 100%)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '12px',
                                    padding: '15px 30px',
                                    fontSize: '1rem',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '10px',
                                    transition: 'all 0.3s'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(232,183,90, 0.5)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'scale(1)';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                            >
                                <RefreshCw style={{ width: '20px', height: '20px' }} />
                                Recharger la page
                            </button>

                            <button
                                onClick={this.handleGoHome}
                                aria-label="Retour à l'accueil"
                                style={{
                                    flex: '1',
                                    minWidth: '200px',
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    color: 'white',
                                    border: '2px solid rgba(255, 255, 255, 0.2)',
                                    borderRadius: '12px',
                                    padding: '15px 30px',
                                    fontSize: '1rem',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '10px',
                                    transition: 'all 0.3s'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                                    e.currentTarget.style.transform = 'scale(1)';
                                }}
                            >
                                <Home style={{ width: '20px', height: '20px' }} />
                                Retour à l'accueil
                            </button>
                        </div>

                        {/* Info footer */}
                        <p style={{
                            color: '#64748b',
                            textAlign: 'center',
                            marginTop: '30px',
                            fontSize: '0.9rem'
                        }}>
                            💡 L'erreur a été enregistrée pour diagnostic.
                            Si le problème persiste, contacte l'admin.
                        </p>
                    </div>
                </div>
            );
        }

        // Si pas d'erreur, afficher les enfants normalement
        return this.props.children;
    }
}

export default ErrorBoundary;